import { z } from "zod";
import { schema as ChartApiSchema } from "./schema/api/chart";
import { schema as ClientFileSchema } from "./schema/api/clientFile";
import { schema as DiscussionsSchema } from "./schema/api/discussions";
import { schema as OrganizationsSchema } from "./schema/api/organizations";
import { schema as ProfileSchema } from "./schema/api/profile";
import { ResourcesPostPreviewSchema } from "./schema/api/resources";
import { RSSFeedItem, RSSFeedSource } from "./schema/api/rss-feeds";
import { schema as SocialSchema } from "./schema/api/social";
import { schema as UserContentSchema } from "./schema/api/userContent";
import { schema as UsersSchema } from "./schema/api/users";
import { LoginUser as LoginUserSchema } from "./schema/authn";
import { ClientFileReport } from "./schema/clientFile";
import { CompassEvent as CompassEventSchema } from "./schema/compassEvent";
import { DiscussionComment, DiscussionListEntry } from "./schema/discussion";
import {
    Document,
    DocumentListEntry,
    FileEntry,
    FileListEntry,
    PrivateFile,
    PublicFile,
} from "./schema/document";
import { Event as EventSchema } from "./schema/event";
import { Notification } from "./schema/notification";
import { Organization } from "./schema/organization";
import {
    ImageContentType,
    Post,
    PostComment,
    PreviewLink as PreviewLinkSchema,
} from "./schema/posts";
import { Reaction, ReactionType, ReactionsList } from "./schema/reaction";
import {
    CurrentUser,
    FriendRequest,
    FriendRequestStatus,
    Role,
    User,
    UserWithFriendId,
} from "./schema/user";
import {
    NewVendor as NewVendorZodSchema,
    VendorList,
    VendorListingEntry,
    Vendor as VendorSchema,
} from "./schema/vendor";

/*
 * Discussions API types
 */
export const DiscussionReplySchema = DiscussionsSchema.reply.input;
export type DiscussionReply = z.infer<typeof DiscussionReplySchema>;

export const DiscussionCreateSchema = DiscussionsSchema.create.input;
export type DiscussionCreate = z.infer<typeof DiscussionCreateSchema>;

export const DiscussionsListSchema = DiscussionsSchema.list.input;
export type DiscussionsList = z.infer<typeof DiscussionsListSchema>;

/*
 * Documents API types
 */
export const DocumentsListSchema = UserContentSchema.list.input;
export type DocumentsList = z.infer<typeof DocumentsListSchema>;

/*
 * Organizations API types
 */
export const OrganizationCreateSchema = OrganizationsSchema.create.input;
export type OrganizationCreate = z.infer<typeof OrganizationCreateSchema>;

export const OrganizationUpdateSchema = OrganizationsSchema.update.input;
export type OrganizationUpdate = z.infer<typeof OrganizationUpdateSchema>;

export const OrganizationsListSchema = OrganizationsSchema.list.input;
export type OrganizationsList = z.infer<typeof OrganizationsListSchema>;

/*
 * Posts API types
 */
export const PostDeleteSchema = SocialSchema.posts.delete.input;
export type PostDelete = z.infer<typeof PostDeleteSchema>;

export const PostUpdateSchema = SocialSchema.posts.update.input;
export type PostUpdate = z.infer<typeof PostUpdateSchema>;

export const PostDeleteCommentSchema = SocialSchema.posts.delete.input;
export type PostDeleteComment = z.infer<typeof PostDeleteCommentSchema>;

export const PostCreateSchema = SocialSchema.posts.create.input;
export type PostCreate = z.infer<typeof PostCreateSchema>;

export const PostCreateCommentSchema = SocialSchema.postComments.create.input;
export type PostCreateComment = z.infer<typeof PostCreateCommentSchema>;

export const PostUpdateCommentSchema = SocialSchema.postComments.update.input;
export type PostUpdateComment = z.infer<typeof PostUpdateCommentSchema>;

/*
 * Blog API types
 */
export type ResourcesPostPreview = z.infer<typeof ResourcesPostPreviewSchema>;

/*
 * Users API types
 */

export const ClientFileListSchema = ClientFileSchema.latest.input;
export type ClientFileList = z.infer<typeof ClientFileListSchema>;

export const UsersListSchema = UsersSchema.list.input;
export type UsersList = z.infer<typeof UsersListSchema>;

export const SocialProfileSchema = UsersSchema.getPublic.output;
export type SocialProfile = z.infer<typeof SocialProfileSchema>;

export const UserEmailUpdateSchema = UsersSchema.updateEmail.input;
export type UserEmailUpdate = z.infer<typeof UserEmailUpdateSchema>;

export const UserPasswordUpdateSchema = UsersSchema.updatePassword.input;
export type UserPasswordUpdate = z.infer<typeof UserPasswordUpdateSchema>;

export const UserBioUpdateSchema = UsersSchema.updateBio.input;
export type UserBioUpdate = z.infer<typeof UserBioUpdateSchema>;

export const UserOrganizationUpdateSchema = UsersSchema.updateOrganization.input;
export type UserOrganizationUpdate = z.infer<typeof UserOrganizationUpdateSchema>;

export const UserAvatarUploadSchema = UsersSchema.uploadAvatar.input;
export type UserAvatarUpload = z.infer<typeof UserAvatarUploadSchema>;

export const VendorListSchema = VendorList.input;
export type VendorList = z.infer<typeof VendorListSchema>;

/*
 * Profile types
 */

export const ProfileUpdateSchema = ProfileSchema.updateProfile.input;
export type ProfileUpdate = z.infer<typeof ProfileUpdateSchema>;

/*
 * Auth types
 */
export { LoginUserSchema };
export type LoginUser = z.infer<typeof LoginUserSchema>;

/*
 * Generic client types
 */
export type ClientCurrentUser = z.infer<typeof CurrentUser>;
export type ClientDiscussionComment = z.infer<typeof DiscussionComment>;
export type ClientDiscussionListEntry = z.infer<typeof DiscussionListEntry>;
export type ClientDocument = z.infer<typeof Document>;
export type ClientFileEntry = z.infer<typeof FileEntry>;
export type ClientPublicFile = z.infer<typeof PublicFile>;
export type ClientPrivateFile = z.infer<typeof PrivateFile>;
export type ClientFileListEntry = z.infer<typeof FileListEntry>;
export type ClientFileReport = z.infer<typeof ClientFileReport>;
export type ClientDocumentListEntry = z.infer<typeof DocumentListEntry>;
export type ClientOrganization = z.infer<typeof Organization>;
export type ClientPost = z.infer<typeof Post>;
export type PreviewLink = z.infer<typeof PreviewLinkSchema>;
export type ClientPostComment = z.infer<typeof PostComment>;
/** @deprecated Use ClientPublicFile instead */
export type ClientImage = ClientPublicFile;
export type ClientUser = z.infer<typeof User>;
export type ClientUserRole = z.infer<typeof Role>;
export type FriendRequestStatus = z.infer<typeof FriendRequestStatus>;
export type Friend = z.infer<typeof UserWithFriendId>;
export type ClientNotification = z.infer<typeof Notification>;
export type ClientFriendRequest = z.infer<typeof FriendRequest>;
export type ClientReactionType = z.infer<typeof ReactionType>;
export type ClientReactionList = z.infer<typeof ReactionsList>;
export type ClientReaction = z.infer<typeof Reaction>;
export { ImageContentType as ImageContentTypeSchema };

export const NewVendorSchema = NewVendorZodSchema;
export type NewVendor = z.infer<typeof NewVendorSchema>;
export type VendorListing = z.infer<typeof VendorListingEntry>;
export type VendorDetailed = z.infer<typeof VendorSchema>;

export type RSSFeed = z.infer<typeof RSSFeedSource>;
export type RSSFeedItem = z.infer<typeof RSSFeedItem>;

export type CalendarEvent = z.infer<typeof EventSchema>;
export type CompassEvent = z.infer<typeof CompassEventSchema>;

/*
 * Charts API types
 */
export const ChartsListSchema = ChartApiSchema.list.input;
export type ChartsList = z.infer<typeof ChartsListSchema>;
